"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Terminal, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function JoinTheNetwork() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="148649" id="strategic-inquiry" className="dark bg-background text-foreground relative min-h-screen flex items-center justify-center py-24 overflow-hidden">
      {}
      <style dangerouslySetInnerHTML={{
      __html: `
          @keyframes dataStream {
            0% { transform: translateY(-100%); opacity: 0; }
            10% { opacity: 0.5; }
            90% { opacity: 0.5; }
            100% { transform: translateY(100%); opacity: 0; }
          }
          .animate-data-stream {
            animation: dataStream linear infinite;
          }
          .command-input-trough {
            background: linear-gradient(to bottom, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 100%);
            box-shadow: inset 0 2px 10px rgba(0,0,0,0.7);
          }
        `
    }} />

      {}
      <div className="absolute inset-0 pointer-events-none">
        {}
        <div className="absolute inset-0 opacity-[0.05]" style={{
        backgroundImage: 'radial-gradient(var(--color-primary) 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }}></div>
        
        {}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-primary/10 rounded-full blur-[140px]"></div>
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-secondary/10 rounded-full blur-[120px]"></div>

        {}
        <div className="absolute inset-0 flex justify-between px-10 md:px-32 opacity-40">
          {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="h-full w-px bg-primary/20 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-48 bg-gradient-to-b from-transparent via-primary to-transparent animate-data-stream" style={{
            animationDuration: `${4 + Math.random() * 5}s`,
            animationDelay: `${Math.random() * 3}s`
          }}></div>
            </div>)}
        </div>
      </div>

      <div ref={ref} className={`relative z-10 w-full max-w-3xl mx-auto px-4 transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center p-4 mb-8 rounded-full bg-primary/10 border border-primary/40 shadow-[0_0_30px_rgba(var(--color-primary),0.3)]">
            <Terminal className="w-10 h-10 text-primary" />
          </div>
          <h2 className="text-5xl md:text-7xl font-default font-bold tracking-tighter uppercase text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent-foreground to-secondary drop-shadow-[0_0_15px_rgba(var(--color-primary),0.6)] mb-6">
            Intel Briefing
          </h2>
          <p className="text-muted-foreground font-sans text-base md:text-xl max-w-2xl mx-auto leading-relaxed">
            Initialize secure briefing parameters. Access sovereign intelligence and enterprise-grade infrastructure protocols through our dedicated inquiry terminal.
          </p>
        </div>

        <div className="bg-card/30 backdrop-blur-2xl border border-border/40 rounded-xl p-1 shadow-2xl shadow-primary/10 relative overflow-hidden">
          {}
          <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/60"></div>
          <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-primary/60"></div>
          <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-primary/60"></div>
          <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-primary/60"></div>

          <div className="bg-background/90 p-8 md:p-12 rounded-lg border border-border/20">
            <FormProvider formKey="strategic_inquiry_form_insights" formId="strategic_inquiry_form_insights" sectionName="Strategic Inquiry" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            company_name: "",
            role: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: false
            },
            company_name: {
              required: false
            },
            role: {
              required: false
            },
            message: {
              required: false
            }
          }} submitText="REQUEST INTEL BRIEFING" successMessage="ENCRYPTED CHANNEL ESTABLISHED. Briefing sequence initiated." errorMessage="UPLINK FAILED. Please verify credentials and re-attempt transmission.">
              <CommandForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function CommandForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 text-center space-y-8 animate-in fade-in zoom-in duration-700">
        <div className="relative">
          <div className="absolute inset-0 bg-primary/30 blur-2xl rounded-full"></div>
          <CircleCheck className="w-20 h-20 text-primary relative z-10" />
        </div>
        <div className="space-y-4">
          <h3 className="text-3xl font-default font-bold text-primary tracking-tighter uppercase">Uplink Secured</h3>
          <p className="text-muted-foreground font-mono text-lg">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-10 font-mono tracking-widest uppercase border-primary/40 text-primary hover:bg-primary/10 rounded-md px-10">
          Return to Console
        </Button>
      </div>;
  }
  const inputClassName = "command-input-trough border-b-2 border-t-0 border-l-0 border-r-0 border-primary/40 rounded-none font-mono text-primary-foreground caret-primary focus-visible:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/40 transition-all h-14 px-4 text-base";
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <FormField control={control} name="full_name" rules={{
        required: "Operator Identity is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs tracking-widest text-primary/70 uppercase flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-primary rounded-full inline-block animate-pulse"></span>
                Operator Identity <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="Enter full name" className={inputClassName} {...field} />
              </FormControl>
              <FormMessage className="font-mono text-xs text-destructive" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Secure Channel is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid transmission format"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs tracking-widest text-primary/70 uppercase flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-primary rounded-full inline-block animate-pulse"></span>
                Secure Channel <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="operator@domain.ai" className={inputClassName} {...field} />
              </FormControl>
              <FormMessage className="font-mono text-xs text-destructive" />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs tracking-widest text-muted-foreground uppercase flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-muted-foreground/30 rounded-full inline-block"></span>
                Comms Frequency
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className={inputClassName} {...field} />
              </FormControl>
              <FormDescription className="font-mono text-[11px] text-muted-foreground/50">
                Optional direct line for priority briefing.
              </FormDescription>
              <FormMessage className="font-mono text-xs text-destructive" />
            </FormItem>} />

        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs tracking-widest text-muted-foreground uppercase flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-muted-foreground/30 rounded-full inline-block"></span>
                Organization
              </FormLabel>
              <FormControl>
                <Input placeholder="Tidal Swing AI" className={inputClassName} {...field} />
              </FormControl>
              <FormMessage className="font-mono text-xs text-destructive" />
            </FormItem>} />
      </div>

      <FormField control={control} name="role" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs tracking-widest text-muted-foreground uppercase flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-muted-foreground/30 rounded-full inline-block"></span>
              System Clearance
            </FormLabel>
            <FormControl>
              <div className="relative">
                <select className={`w-full appearance-none ${inputClassName}`} {...field}>
                  <option value="" disabled className="bg-background text-muted-foreground">Select clearance level...</option>
                  {["Enterprise Executive", "Infrastructure Architect", "Government Liaison", "Product Visionary", "Other"].map((option, i) => <option key={i} data-index={i} value={option} className="bg-background text-foreground">
                      {option}
                    </option>)}
                </select>
                <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none">
                  <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-secondary"></div>
                </div>
              </div>
            </FormControl>
            <FormMessage className="font-mono text-xs text-destructive" />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs tracking-widest text-muted-foreground uppercase flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-muted-foreground/30 rounded-full inline-block"></span>
              Mission Parameters
            </FormLabel>
            <FormControl>
              <textarea placeholder="Define inquiry parameters and strategic objectives..." className={`w-full min-h-[140px] py-4 resize-y ${inputClassName}`} {...field} />
            </FormControl>
            <FormMessage className="font-mono text-xs text-destructive" />
          </FormItem>} />

      {submitError && <div className="p-5 bg-destructive/10 border border-destructive/40 rounded-md flex items-start gap-4">
          <CircleAlert className="w-6 h-6 text-destructive shrink-0 mt-0.5" />
          <p className="text-destructive font-mono text-sm leading-relaxed">{submitError}</p>
        </div>}

      <div className="pt-6">
        <Button type="submit" disabled={isSubmitting} className="w-full h-16 font-mono text-lg tracking-[0.25em] uppercase bg-primary/10 text-primary border border-primary/50 hover:bg-primary hover:text-primary-foreground shadow-[0_0_25px_rgba(var(--color-primary),0.2)] hover:shadow-[0_0_40px_rgba(var(--color-primary),0.5)] transition-all duration-500 relative overflow-hidden group rounded-md">
          <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-primary/30 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></div>
          {isSubmitting ? <span className="flex items-center gap-4">
              <Loader className="w-6 h-6 animate-spin" />
              SYNCHRONIZING...
            </span> : <span className="flex items-center gap-4">
              <Terminal className="w-6 h-6 opacity-80" />
              REQUEST INTEL BRIEFING
            </span>}
        </Button>
      </div>
    </Form>;
}